import { axios } from "@cores/axiosWrapper";

/**
 * 웹훅 목록 조회.
 * @param {string} stageId - 스테이지 ID
 * @param {string} apiEndpoint - API 엔드포인트
 * @param {string} projectId - 프로젝트 ID
 * @param {object} params - { name, status, limit, nextToken }
 *   - name: 이름 부분 일치 검색(서버)
 *   - status: ACTIVE | INACTIVE (그 외 값은 400)
 *   - limit: 1~100, 기본 20
 *   - nextToken: 토큰 기반 페이지네이션(offset/page 미지원)
 * @returns {Promise} { results: WebhookSummary[], nextToken }
 *   ⚠️ Summary 에는 description / configuration.headers / configuration.method 가 없다.
 *      상세 화면에 재사용하지 말고 단건 조회를 사용할 것.
 */
export const getWebhooksAPI = (stageId, apiEndpoint, projectId, params = {}) => {
    return axios.get(`${apiEndpoint}/webhooks`, {
        params,
        headers: {
            stageId,
            projectId,
        },
    });
};

/**
 * 웹훅 상세 조회.
 * @param {string} webhookId - 웹훅 ID(hash-id 불투명 문자열 — 정렬 키로 쓰지 말 것)
 * @returns {Promise} Webhook (Summary + description + configuration.headers/method)
 */
export const getWebhookAPI = (stageId, apiEndpoint, projectId, webhookId) => {
    return axios.get(`${apiEndpoint}/webhooks/${webhookId}`, {
        headers: {
            stageId,
            projectId,
        },
    });
};

/**
 * 웹훅 생성.
 * @param {object} data - WebhookRequest { name, description, status, triggers, configuration }
 * @returns {Promise} 201 { id } — ⚠️ 전체 객체가 아니라 id 만 온다. 목록 반영은 재조회로.
 */
export const createWebhookAPI = (stageId, apiEndpoint, projectId, data) => {
    return axios.post(`${apiEndpoint}/webhooks`, data, {
        headers: {
            stageId,
            projectId,
        },
    });
};

/**
 * 웹훅 수정(부분 수정).
 * @param {string} webhookId - 웹훅 ID
 * @param {object} body - 변경된 필드만. endpoint/headers 는 configuration 아래 중첩.
 *   예) { name } · { status } · { triggers } · { configuration: { endpoint } }
 *   ⚠️ additionalProperties:false 라 스키마에 없는 키를 보내면 400.
 *   ⚠️ OpenAPI 문서에는 "전체 교체"로 서술돼 있으나 실제 구현은 부분 수정(계획서 §2 결정 1 참고).
 * @returns {Promise} 204 No Content — 응답 본문이 없으므로 성공 후 상세 재조회 필요.
 */
export const updateWebhookAPI = (stageId, apiEndpoint, projectId, webhookId, body) => {
    return axios.patch(`${apiEndpoint}/webhooks/${webhookId}`, body, {
        headers: {
            stageId,
            projectId,
        },
    });
};

/**
 * 웹훅 삭제.
 * @returns {Promise} 204 No Content. 되돌릴 수 없고 전송 이력도 함께 제거된다.
 */
export const deleteWebhookAPI = (stageId, apiEndpoint, projectId, webhookId) => {
    return axios.delete(`${apiEndpoint}/webhooks/${webhookId}`, {
        headers: {
            stageId,
            projectId,
        },
    });
};

/**
 * 웹훅 트리거 카탈로그 조회.
 * 프로젝트와 무관한 고정 카탈로그이므로 한 번 받아 재사용한다.
 * @param {object} params - { names, limit, nextToken } (names 는 쉼표 구분 다중)
 * @returns {Promise} { results: [{ name, event }], nextToken }
 *   - event 는 JSON Schema draft-07 문서이며 요청 본문 예시는 event.examples[] 에 담긴다.
 */
export const getWebhookTriggersAPI = (stageId, apiEndpoint, projectId, params = {}) => {
    return axios.get(`${apiEndpoint}/webhook-triggers`, {
        params,
        headers: {
            stageId,
            projectId,
        },
    });
};
